import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs/esm/index';
import utc from 'dayjs/esm/plugin/utc';

dayjs.extend(utc);

/**
 * Local standalone ports of the AngularJS filters the FROZEN baseline
 * `account-v1/.../components/curation/curationModal.html` binds in its primary
 * `#curation` region. Kept LOCAL + standalone (the same self-containment rule the
 * curation card follows, see `features/stories/curation-card/curation-card.pipes.ts`)
 * so the verbatim native curation modal never routes through the eval-order-fragile
 * account-shared barrel ([[project_account_barrel_eval_order_fragility]]). Each is a
 * byte-faithful port of the v1 filter the frozen baseline renders with, so the
 * candidate's text matches v1 character-for-character (pixel parity).
 *
 * The `| translate` pipe is now the native ngx `TranslateModule` (the WS2 translate
 * sweep drained `CurationTranslatePipe`); `WinnerPlacePipe` likewise builds its
 * place-label map from the native ngx `TranslateService.instant`
 * ([[project_account_ngx_translate_gap]]).
 */

/**
 * v1 `winnerPlace` (`sections/platform/.../winnerSummary.directive.js`, now relocated to
 * `components/shared-features/`) — maps a place code to a translated label via
 * `parse.int(place)`. map keys: 1/2/3 = 1ST/2ND/3RD place, 100 = runner-up.
 */
@Pipe({ name: 'winnerPlace' })
export class WinnerPlacePipe implements PipeTransform {
  private readonly map: { [key: number]: string };

  constructor() {
    const translateSvc = inject(TranslateService);
    const translate = (key: string) => translateSvc.instant(key);
    this.map = {
      1: translate('flipto.account.sections.platform.photoAdvocacy.1STPlace'),
      2: translate('flipto.account.sections.platform.photoAdvocacy.2NDPlace'),
      3: translate('flipto.account.sections.platform.photoAdvocacy.3RDPlace'),
      100: translate('flipto.account.sections.platform.photoAdvocacy.RunnerUp'),
    };
  }

  transform(place: any): string {
    return this.map[parseInt(place, 10)];
  }
}

/**
 * v1 `dateRange` (`components/common/date-range/dateRange.filter.js`) — `moment.utc`
 * start/end; start drops the year when both share a year, end always keeps it.
 */
@Pipe({ name: 'dateRange' })
export class CurationDateRangePipe implements PipeTransform {
  transform(start: any, end: any): string {
    const formatNoYear = 'MMM D';
    const formatWithYear = 'MMM D, YYYY';
    const startMoment = dayjs.utc(start);
    const endMoment = dayjs.utc(end);
    const isSameYear = startMoment.year() === endMoment.year();
    const startFormatted = isSameYear
      ? startMoment.format(formatNoYear)
      : startMoment.format(formatWithYear);
    const endFormatted = endMoment.format(formatWithYear);
    return startFormatted + ' - ' + endFormatted;
  }
}

/**
 * v1 `dateFormat` (`components/common/date-range/dateFormat.filter.js`) — `moment.utc`
 * by default; `format` defaults to `MMM D`.
 */
@Pipe({ name: 'dateFormat' })
export class CurationDateFormatPipe implements PipeTransform {
  transform(date: any, format?: string, isUtc = true): string {
    const fmt = format || 'MMM D';
    const momentDate = isUtc ? dayjs.utc(date) : dayjs(date);
    return momentDate.format(fmt);
  }
}

/**
 * v1 `isNowInRange` — `now.isAfter(start) && now.isBefore(end)` (local standalone copy
 * of `../pipes/is-now-in-range.pipe.ts`, which is module-declared, not standalone).
 */
@Pipe({ name: 'isNowInRange' })
export class CurationIsNowInRangePipe implements PipeTransform {
  transform(start: any, end?: any): boolean {
    const now = dayjs();
    return now.isAfter(start) && now.isBefore(end);
  }
}
